// "New project": the shared create form in a dialog. On success the projects
// lists refresh and we land in the welcome's project-level steps (workflows,
// boards, agent) for the new project; they end at its overview.

import { useNavigate } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { githubApi } from '../../lib/api-client'
import { useAuth, useWorkspaceId } from '../../lib/auth'
import { useCurrentProject } from '../../lib/project-context'
import { Dialog } from '../ui'
import {
  CreateProjectForm,
  PROJECT_MANAGER_ROLES,
  useCreateProjectForm,
} from './create-project-form'

/** Whether the signed-in user may create projects (owner or administrator). */
export function useCanCreateProject(): boolean {
  const { user } = useAuth()
  return (user?.roles ?? []).some((role) => PROJECT_MANAGER_ROLES.includes(role))
}

export function NewProjectDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const workspaceId = useWorkspaceId()
  const { projects } = useCurrentProject()

  const githubQuery = useQuery({
    queryKey: ['github', workspaceId],
    queryFn: () => githubApi.status(workspaceId),
    enabled: open && workspaceId !== '',
  })
  const githubConnected =
    githubQuery.data?.apps.some((a) => a.installations.length > 0) || githubQuery.data?.token != null

  const controller = useCreateProjectForm({
    workspaceId,
    projects,
    githubConnected,
    onCreated: (project) => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      onClose()
      void navigate({ to: '/welcome', search: { step: 'workflows', project: project.key } })
    },
  })

  function close(): void {
    if (controller.pending) return
    controller.create.reset()
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      size="md"
      title="New project"
      description="One codebase: its repository, its rules and its boards. Next you set up its workflows and boards."
    >
      <div className="space-y-4">
        <CreateProjectForm controller={controller} formId="new-project-form" idPrefix="new" />
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={close}
            disabled={controller.pending}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="new-project-form"
            className="btn-primary"
            disabled={!controller.canSubmit}
          >
            {controller.pending ? 'Creating…' : 'Create project'}
          </button>
        </div>
      </div>
    </Dialog>
  )
}
